/**
 * Phase 5 PR 5.2 — `<EmptyMacroPlan />` discovery card.
 * SP-3 C3 (2026-07-12) — restyled to the ratified empty-state anatomy
 * (first-plan/README.md §empty-states + frame 1i): one voice, skyline glyph,
 * "YOUR SEASON" kicker, Instrument Serif title, ≤2-line body, one marine
 * primary, one quiet secondary. Purely presentational — the create/generate
 * logic, the state-dependent CTA labels, and the retryable-error line are
 * unchanged (the spec covers neither the dynamic labels nor the error line, so
 * both keep their prior behavior). The meet badge and the labeled
 * SwimlaneSkeleton are retired: the anatomy carries no badge, and both variants
 * now share the decorative (unlabeled) skyline glyph, which keeps G3's
 * "no phase system pre-opt-in" intact for the no-season card.
 *
 * Shown at `/v2/plan` (+ the `/v2/today` no-season empty-state).
 *
 * Generate UX rules followed:
 *   1. Disabled while pending (`disabled`)
 *   3. Retryable error state — `lastError`/`startError`, when non-null,
 *      surfaces with a Retry CTA.
 *
 * Idempotent-on-duplicate (rule 2) is enforced at the route + backend.
 */

import { ReplanRefusalLine } from './ReplanRefusalLine';
import {
  GUARDIAN_HOLD_MESSAGE,
  AGE_BELOW_MINIMUM_MESSAGE,
  AGE_BAND_REQUIRED_MESSAGE,
  AGE_REVIEW_PENDING_MESSAGE,
  NO_ACTIVE_SEASON,
  NO_ACTIVE_SEASON_MESSAGE,
} from '../../lib/planEdit';
import type { CodedError } from '../../lib/roleRefusal';
import styles from './plan.module.css';

interface BaseProps {
  /** Retained for call-site compatibility; the anatomy carries no meet badge
   *  (spec frame 1i), so the count is no longer surfaced here. */
  meetCount: number;
  /**
   * ⚖️ R71 — the ask was refused with `409 replan_blocked_in_flight`. The
   * control SETTLES BACK TO REST (no spinner, no permanent disable, no retry
   * affordance) and one quiet line renders beneath it. The caller passes a
   * null error alongside this, so nothing here reads as an incident.
   */
  refused?: boolean;
  /**
   * ⚖️ B99 — the account is HELD, not the ask: the server answered the B94
   * COPPA gate with 403 `guardian_consent_required`. Same treatment as a
   * refusal, one line further: the control settles back to rest and the quiet
   * line carries `GUARDIAN_HOLD_MESSAGE`. A minor reads this card, and there
   * is nothing here she can retry — her guardian clears it, not she.
   */
  held?: boolean;
  /**
   * B196 L5 (spec §4.1, F8) — RULE 1, the floor: an existing account's write
   * was refused `age_below_minimum`. Same treatment as `held` — a quiet line
   * beneath the control, no retry — but a different sentence and a different
   * code, so it is its own prop rather than collapsed into `held`.
   */
  ageBelowMinimum?: boolean;
  /**
   * B196 L5 (spec §4.2, F8) — RULE 2, the unknown-age wall: no established
   * age band on the request path, refused `age_band_required`.
   */
  ageBandRequired?: boolean;
  /**
   * B196 L9 (spec §4.5, §4.8) — RULE 2.6a, the actual-knowledge review hold.
   * Same treatment as `ageBelowMinimum` / `ageBandRequired` — a quiet line,
   * no retry, its own code and sentence.
   */
  ageReviewPending?: boolean;
}

interface NoPlanProps extends BaseProps {
  /** Season exists, macro status 'none' → generate into it (the original). */
  variant?: 'no-plan';
  onGenerate: () => void;
  generating: boolean;
  lastError: Error | null;
}

interface NoSeasonProps extends BaseProps {
  /** No active season at all → create + generate in one chained action. */
  variant: 'no-season';
  onStartSeason: () => void;
  /** True from click until create+generate settle. Disables the CTA. */
  starting: boolean;
  /** Granular phase for the CTA label. */
  startPhase: 'idle' | 'creating' | 'generating';
  startError: Error | null;
  /** Optional secondary "Add a goal race first" affordance. */
  onAddRace?: () => void;
}

type EmptyMacroPlanProps = NoPlanProps | NoSeasonProps;

/** Decorative skyline glyph — 5 unlabeled mode-tinted slices (frame 1i). */
function SeasonSkyline() {
  return (
    <div className={`${styles.esGlyph} ${styles.esSkyline}`} aria-hidden="true">
      <span className={`${styles.esSkylineSlice} ${styles.esSliceBase}`} />
      <span className={`${styles.esSkylineSlice} ${styles.esSliceBuild}`} />
      <span className={`${styles.esSkylineSlice} ${styles.esSliceComp}`} />
      <span className={`${styles.esSkylineSlice} ${styles.esSliceTaper}`} />
      <span className={`${styles.esSkylineSlice} ${styles.esSliceRecovery}`} />
    </div>
  );
}

export function EmptyMacroPlan(props: EmptyMacroPlanProps) {
  // ─── no-season: create a season + generate its plan ───
  if (props.variant === 'no-season') {
    const { onStartSeason, starting, startPhase, startError, onAddRace, refused, held, ageBelowMinimum, ageBandRequired, ageReviewPending } = props;
    const ctaLabel = starting
      ? startPhase === 'generating'
        ? 'Drafting your plan…'
        : 'Starting your season…'
      : startError && !held && !ageBelowMinimum && !ageBandRequired && !ageReviewPending
        ? 'Try again'
        : 'Start my season';
    return (
      <section className={styles.esCard} aria-label="Start your season">
        <SeasonSkyline />
        <div className={styles.esKicker}>Your season</div>
        <h2 className={styles.esTitle}>Start your season.</h2>
        {/* Register pile 3 (2026-09-10) — a comma where the em dash was. The
            house register carries no em or en dash in athlete-facing text,
            and this card is the first sentence a reader with no season gets.
            The words are the ratified ones (SP-3 C3), unchanged. The
            no-plan variant below carries the same sentence. */}
        <p className={styles.esBody}>
          A phase plan anchored to your goal race, or a 14-week block you can
          re-anchor any time.
        </p>
        <div className={styles.esActions}>
          <button
            type="button"
            className={styles.esPrimary}
            onClick={onStartSeason}
            disabled={starting}
          >
            {ctaLabel}
          </button>
          {onAddRace ? (
            <button
              type="button"
              className={styles.esSecondary}
              onClick={onAddRace}
              disabled={starting}
            >
              Add a goal race first ›
            </button>
          ) : null}
        </div>
        {/* R71 — a refusal is a line, not an event: no sheet, no toast, no
            red, no retry. Every OTHER failure keeps the block it had. */}
        {held ? (
          <ReplanRefusalLine message={GUARDIAN_HOLD_MESSAGE} />
        ) : ageBelowMinimum ? (
          <ReplanRefusalLine message={AGE_BELOW_MINIMUM_MESSAGE} />
        ) : ageBandRequired ? (
          <ReplanRefusalLine message={AGE_BAND_REQUIRED_MESSAGE} />
        ) : ageReviewPending ? (
          <ReplanRefusalLine message={AGE_REVIEW_PENDING_MESSAGE} />
        ) : refused ? <ReplanRefusalLine /> : startError ? (
          <div className={styles.esError} role="alert">
            Couldn&rsquo;t start your season: {startError.message}
          </div>
        ) : null}
      </section>
    );
  }

  // ─── no-plan: season exists, generate its macro plan (original) ───
  const { onGenerate, generating, lastError, refused, held, ageBelowMinimum, ageBandRequired, ageReviewPending } = props;
  const ctaLabel =
    generating
      ? 'Generating…'
      : lastError && !held && !ageBelowMinimum && !ageBandRequired && !ageReviewPending
        ? 'Try again'
        : 'Generate season plan';
  return (
    <section className={styles.esCard} aria-label="Build your season plan">
      <SeasonSkyline />
      <div className={styles.esKicker}>Your season</div>
      <h2 className={styles.esTitle}>Build your season plan.</h2>
      {/* Register pile 3 (2026-09-10) — the same comma as the no-season
          variant above: the two cards say one sentence, so they change
          together or they drift. */}
      <p className={styles.esBody}>
        A phase plan anchored to your goal race, or a 14-week block you can
        re-anchor any time.
      </p>
      <div className={styles.esActions}>
        <button
          type="button"
          className={styles.esPrimary}
          onClick={onGenerate}
          disabled={generating}
        >
          {ctaLabel}
        </button>
      </div>
      {/* R71 — a refusal is a line, not an event. */}
      {held ? (
        <ReplanRefusalLine message={GUARDIAN_HOLD_MESSAGE} />
      ) : ageBelowMinimum ? (
        <ReplanRefusalLine message={AGE_BELOW_MINIMUM_MESSAGE} />
      ) : ageBandRequired ? (
        <ReplanRefusalLine message={AGE_BAND_REQUIRED_MESSAGE} />
      ) : ageReviewPending ? (
        <ReplanRefusalLine message={AGE_REVIEW_PENDING_MESSAGE} />
      ) : refused ? <ReplanRefusalLine /> : lastError ? (
        <div className={styles.esError} role="alert">
          {(lastError as CodedError).code === NO_ACTIVE_SEASON ? (
            NO_ACTIVE_SEASON_MESSAGE
          ) : (
            <>Couldn&rsquo;t generate plan: {lastError.message}</>
          )}
        </div>
      ) : null}
    </section>
  );
}
